/**
 * API client utility for making authenticated requests to the AnyPlay backend.
 */

import config from "../config";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

interface ApiOptions {
  body?: unknown;
  headers?: Record<string, string>;
}

let getAccessToken: (() => Promise<string | null>) | null = null;

/** Register the token getter function (called by DualAuthContext on init) */
export function registerTokenGetter(getter: () => Promise<string | null>) {
  getAccessToken = getter;
}

/** Error thrown by the API client, carrying the HTTP status and parsed body. */
export interface ApiError extends Error {
  status?: number;
  body?: { error?: string; message?: string; [key: string]: unknown };
}

/**
 * Extract a human-readable message from an unknown thrown value.
 * Prefers the API error body's `error`/`message`, then the Error message.
 */
export function getErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  if (err && typeof err === "object") {
    const e = err as ApiError;
    if (e.body?.error) return e.body.error;
    if (e.body?.message) return e.body.message;
    if (typeof e.message === "string" && e.message) return e.message;
  }
  return fallback;
}

async function request<T>(method: HttpMethod, path: string, options?: ApiOptions): Promise<T> {
  const url = `${config.apiGateway.URL}${path}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options?.headers,
  };

  // Attach authorization token
  if (getAccessToken) {
    const token = await getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    method,
    headers,
    body: options?.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: response.statusText }));
    const error: ApiError = new Error(errorBody.message || errorBody.error || `HTTP ${response.status}`);
    error.status = response.status;
    error.body = errorBody;
    throw error;
  }

  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, options?: ApiOptions) => request<T>("GET", path, options),
  post: <T>(path: string, options?: ApiOptions) => request<T>("POST", path, options),
  put: <T>(path: string, options?: ApiOptions) => request<T>("PUT", path, options),
  delete: <T>(path: string, options?: ApiOptions) => request<T>("DELETE", path, options),
};
